import { Outlet, createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import { AdminAnalytics } from '@/components/Analytics/AdminAnalytics';
import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { useIsExactRouteMatch } from '@/hooks/useIsExactRouteMatch';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

import type { DashboardStaticData } from '../route';

const analyticsSearchSchema = z.object({
  aPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  aPageIndex: fallback(z.number(), 0).default(0),
  aSearch: searchParam().optional(),
  aLanguage: searchParam().optional(),
});

const staticData: DashboardStaticData = {
  navbarTitle: 'Git Analytics',
};

export const Route = createFileRoute('/$organizationName/_dashboard/analytics')({
  component: AnalyticsLayout,
  validateSearch: zodValidator(analyticsSearchSchema),
  staticData,
});

// Body-only — the _dashboard layout provides the LayoutDefault chrome, header,
// and SourcesNav. The admin entitlement gate and the drilldown <Outlet/> (the
// codebase analytics sheet) are kept here.
function AnalyticsLayout() {
  const { organizationDisplayName } = useUser();
  const isExactMatch = useIsExactRouteMatch(Route.id);

  useEffect(() => {
    if (isExactMatch) {
      document.title = `${organizationDisplayName || 'Driver'} - Git Analytics`;
    }
  }, [organizationDisplayName, isExactMatch]);

  return (
    <RequireEntitlements admins={['isSuperAdmin', 'isSourceAdmin']}>
      <AdminAnalytics />
      <Outlet />
    </RequireEntitlements>
  );
}
